import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { tv, type VariantProps } from "@ui/lib/tv";
import { ToggleButton } from "@ui/primitives/ToggleButton";

const toolbar = tv({
  slots: {
    root: "flex items-center gap-0.5 border-b border-neutral-100 px-2 py-1.5",
    separator: "mx-1 h-5 w-px bg-neutral-200",
    button: [
      "flex size-8 items-center justify-center rounded-lg text-neutral-500 outline-none transition",
      "hover:bg-neutral-50 hover:text-neutral-800",
      "pressed:bg-neutral-100",
      "focus-visible:ring-2 focus-visible:ring-neutral-300",
      "disabled:pointer-events-none disabled:opacity-40",
    ],
  },
  variants: {
    // Active formatting is the one accent — it adopts the field's identity colour so "this is on"
    // reads in the same hue as the focus halo. Hover/idle stay neutral so the toolbar keeps quiet.
    color: {
      red: { button: "selected:bg-red-100 selected:text-red-700" },
      green: { button: "selected:bg-green-100 selected:text-green-700" },
      purple: { button: "selected:bg-purple-100 selected:text-purple-700" },
    },
  },
  defaultVariants: { color: "purple" },
});

type ToolbarVariants = VariantProps<typeof toolbar>;

interface ToolbarProps {
  editor: Editor;
  color?: ToolbarVariants["color"];
  isDisabled?: boolean;
}

export function Toolbar({ editor, color = "purple", isDisabled }: ToolbarProps) {
  const styles = toolbar({ color });
  const state = useEditorState({
    editor,
    selector: ({ editor: e }: { editor: Editor }) => ({
      isBold: e.isActive("bold"),
      isItalic: e.isActive("italic"),
      isUnderline: e.isActive("underline"),
      isStrike: e.isActive("strike"),
      isBulletList: e.isActive("bulletList"),
      isOrderedList: e.isActive("orderedList"),
    }),
  });

  return (
    <div className={styles.root()} role="toolbar" aria-label={t("components.rich_text_editor.toolbar")}>
      <ToggleButton
        aria-label={t("components.rich_text_editor.bold")}
        className={styles.button()}
        isDisabled={isDisabled}
        isSelected={state.isBold}
        onChange={() => editor.chain().focus().toggleBold().run()}
      >
        <Icon name="bold" size="16" />
      </ToggleButton>
      <ToggleButton
        aria-label={t("components.rich_text_editor.italic")}
        className={styles.button()}
        isDisabled={isDisabled}
        isSelected={state.isItalic}
        onChange={() => editor.chain().focus().toggleItalic().run()}
      >
        <Icon name="italic" size="16" />
      </ToggleButton>
      <ToggleButton
        aria-label={t("components.rich_text_editor.underline")}
        className={styles.button()}
        isDisabled={isDisabled}
        isSelected={state.isUnderline}
        onChange={() => editor.chain().focus().toggleUnderline().run()}
      >
        {/* The underline glyph is box-centred, so its trailing bar pushes the letter's cap-line
            up ~5% vs the other icons. Nudge down so the "U" top sits on the shared cap-line. */}
        <Icon className="translate-y-[5%]" name="underline" size="16" />
      </ToggleButton>
      <ToggleButton
        aria-label={t("components.rich_text_editor.strikethrough")}
        className={styles.button()}
        isDisabled={isDisabled}
        isSelected={state.isStrike}
        onChange={() => editor.chain().focus().toggleStrike().run()}
      >
        <Icon name="strikethrough" size="16" />
      </ToggleButton>

      <div className={styles.separator()} />

      <ToggleButton
        aria-label={t("components.rich_text_editor.bullet_list")}
        className={styles.button()}
        isDisabled={isDisabled}
        isSelected={state.isBulletList}
        onChange={() => editor.chain().focus().toggleBulletList().run()}
      >
        <Icon name="listBullet" size="16" />
      </ToggleButton>
      <ToggleButton
        aria-label={t("components.rich_text_editor.ordered_list")}
        className={styles.button()}
        isDisabled={isDisabled}
        isSelected={state.isOrderedList}
        onChange={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <Icon name="listNumbered" size="16" />
      </ToggleButton>
    </div>
  );
}
